/** The signed-in user and how one can sign in, from GET /api/v1/me. */
import { createContext, useContext, type ReactNode } from 'react'
import { api } from './api'
import { invalidate, useQuery } from './query'
import { atLeast, type AuthProvider, type Role } from '../../shared/model'

export interface Me {
  id: string
  name: string
  avatar_url: string
  email: string | null
  role: Role
  show_explicit: boolean
  collection_public: boolean
  edits_applied: number
  banned: boolean
}

export interface SessionInfo {
  user: Me | null
  providers: AuthProvider[]
  dev_login: boolean
  trust_threshold: number
  /** Whether this server catalogues wargames (WARGAMES). Off: the game switch and wargame fields are hidden. */
  wargames: boolean
  /** CONTACT_EMAIL: where the terms and privacy pages send people. Empty when the server sets none. */
  contact_email: string
}

interface SessionValue extends Partial<SessionInfo> {
  loading: boolean
  can: (role: Role) => boolean
  signOut: () => Promise<void>
  refresh: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, refresh } = useQuery<SessionInfo>('/api/v1/me', { staleMs: 60_000 })
  const value: SessionValue = {
    ...data,
    loading,
    can: (role) => atLeast(data?.user?.role, role),
    signOut: async () => {
      await api('/auth/logout', { method: 'POST' })
      invalidate('/api/')
    },
    refresh: () => void refresh(),
  }
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext)
  if (!v) throw new Error('useSession outside SessionProvider')
  return v
}

/** Where to send someone to sign in, returning them here afterwards. */
export const signInHref = (returnTo: string) => `/signin?return=${encodeURIComponent(returnTo)}`
